// Keep the pure calculation contract in sync with noc-strapi/src/utils/supplierPay.js.
const SCALE = 1000000n;
const decimal = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const text = String(value).trim();
  if (!/^\d{1,12}(\.\d{1,6})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  return BigInt(whole) * SCALE + BigInt(fraction.padEnd(6, '0'));
};
const number = (value) => Number(value) / Number(SCALE);
const normalizeEngagement = (value) => {
  const type = String(value || '')
    .trim()
    .toLowerCase();
  if (type === 'employee') return 'employee';
  if (['self_employed', 'subcontractor'].includes(type)) return 'self_employed';
  return null;
};
const engagementAt = (supplier, date) => {
  const history = Array.isArray(supplier?.engagementHistory)
    ? supplier.engagementHistory
    : [];
  if (history.length) {
    const periods = [...history].sort((a, b) => String(a.from).localeCompare(String(b.from)));
    const period = [...periods].reverse().find((entry) => entry.from <= date && (!entry.to || date <= entry.to));
    // The first Self-employed selection is a classification, not an employment start date.
    // Keep subsequent Employee/Self-employed transitions effective on their recorded dates.
    if (!period && date < periods[0].from && normalizeEngagement(periods[0].type) === 'self_employed')
      return 'self_employed';
    return normalizeEngagement(period?.type);
  }
  return normalizeEngagement(supplier?.rightToWorkEngagementType);
};
const appointmentHours = (item) => item?.fixedHours ?? item?.hours ?? null;
const supplierPayRate = (service, type) =>
  type === 'employee'
    ? service?.employeeRate
    : type === 'self_employed'
      ? service?.supplierRate
      : null;
const buildPaySummary = (appointments = [], { type = null } = {}) => {
  const lines = appointments.map((item) => {
    const engagementType = type || engagementAt(item.supplier, item.date);
    const hours = appointmentHours(item);
    const rate = supplierPayRate(item.service, engagementType);
    const errors = [];
    if (!engagementType) errors.push(normalizeEngagement(item.supplier?.rightToWorkEngagementType)
      ? 'No engagement type covers this appointment date. Check Engagement effective from in Suppliers.'
      : 'Select an engagement type in Suppliers.');
    if (!item.service?.id && !item.service?.documentId)
      errors.push('Select a service in Appointments.');
    if (decimal(hours) === null)
      errors.push('Enter valid hours in Appointments.');
    if (engagementType && decimal(rate) === null)
      errors.push(
        `Set ${engagementType === 'employee' ? 'Employee' : 'Subcontractor'} pay in Services.`,
      );
    return {
      appointmentId: String(item.documentId || item.id),
      date: item.date,
      buildingId: item.building?.documentId || item.building?.id || null,
      buildingName: item.building?.name || '',
      serviceId: item.service?.documentId || item.service?.id || null,
      serviceName: item.service?.name || '',
      supplierId: item.supplier?.documentId || item.supplier?.id || null,
      supplierName: item.supplier?.name || '',
      flatRef: item.flatRef || '',
      hours,
      rate,
      engagementType,
      amount: null,
      errors,
    };
  });
  const groups = new Map();
  for (const line of lines) {
    const key = `${line.serviceId}|${line.engagementType}|${line.rate}`;
    if (!groups.has(key))
      groups.set(key, {
        key,
        serviceId: line.serviceId,
        serviceName: line.serviceName,
        engagementType: line.engagementType,
        rate: line.rate,
        lines: [],
      });
    groups.get(key).lines.push(line);
  }
  let totalCents = 0n;
  const rows = [...groups.values()].map((group) => {
    const units = group.lines.reduce(
      (sum, line) => sum + (decimal(line.hours) ?? 0n),
      0n,
    );
    const invalid = group.lines.some((line) => line.errors.length);
    let cents = null;
    if (!invalid) {
      const rate = decimal(group.rate);
      const divisor = SCALE * SCALE;
      cents = (units * rate * 100n + divisor / 2n) / divisor;
      const ordered = [...group.lines].sort((a, b) =>
        a.appointmentId.localeCompare(b.appointmentId),
      );
      let assigned = 0n;
      for (const line of ordered) {
        const value = (decimal(line.hours) * rate * 100n) / divisor;
        line.amount = Number(value) / 100;
        assigned += value;
      }
      for (let i = 0; assigned < cents; i += 1, assigned += 1n) {
        const line = ordered[i % ordered.length];
        line.amount = Math.round(line.amount * 100 + 1) / 100;
      }
      totalCents += cents;
    }
    return {
      ...group,
      lines: undefined,
      hours: number(units),
      amount: cents === null ? null : Number(cents) / 100,
      errors: [...new Set(group.lines.flatMap((line) => line.errors))],
    };
  });
  return {
    lines,
    groups: rows,
    totalHours: number(
      lines.reduce((sum, line) => sum + (decimal(line.hours) ?? 0n), 0n),
    ),
    totalAmount: lines.some((line) => line.errors.length)
      ? null
      : Number(totalCents) / 100,
  };
};

export {
  decimal,
  normalizeEngagement,
  engagementAt,
  appointmentHours,
  supplierPayRate,
  buildPaySummary,
};
